"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Sparkles } from "lucide-react";

const QUERIES = [
  { q: "„Seite lädt langsam“", hit: "➔ Performance & LCP" },
  { q: "„KI ohne Cloud“", hit: "➔ Offline AI & WebNN" },
  { q: "„Tastatur-Fokus fehlt“", hit: "➔ Barrierefreiheit" },
];

/**
 * 3D-Bühne im Hintergrund + gestochen scharfe Intent-Spotlight-Suche im Vordergrund.
 */
export default function IntuitiveSearchPreview() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % QUERIES.length);
    }, 2500);
    return () => clearInterval(timer);
  }, []);

  const current = QUERIES[index];

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{ scale: [1, 1.22, 1], opacity: [0.45, 0.85, 0.45] }}
        transition={{ duration: 4.5, repeat: Infinity }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-purple-500/30 blur-3xl"
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(168,85,247,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(168,85,247,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <div className="relative z-10 flex w-60 flex-col items-center gap-2.5 antialiased">
        <div className="flex w-full items-center justify-between gap-2 rounded-2xl border border-purple-400/80 bg-white px-3.5 py-2 shadow-[0_14px_28px_-6px_rgba(168,85,247,0.5)] dark:bg-zinc-900">
          <div className="flex items-center gap-2 overflow-hidden">
            <Search className="h-3.5 w-3.5 shrink-0 text-purple-500" />
            <AnimatePresence mode="wait">
              <motion.span
                key={index}
                initial={{ y: 8, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -8, opacity: 0 }}
                transition={{ duration: 0.22 }}
                className="truncate font-sans text-xs font-extrabold text-neutral-900 dark:text-white"
              >
                {current.q}
              </motion.span>
            </AnimatePresence>
          </div>
          <span className="rounded bg-purple-500/15 px-1.5 py-0.5 font-mono text-[9px] font-black text-purple-600 dark:text-purple-300">
            ↵
          </span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={`hit-${index}`}
            initial={{ y: 6, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-1.5 rounded-full border border-emerald-400/80 bg-white px-3 py-1 text-[11px] font-black text-emerald-600 shadow-lg dark:bg-zinc-900 dark:text-emerald-400"
          >
            <Sparkles className="h-3 w-3 text-emerald-500" />
            <span>{current.hit}</span>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
